import { UserFriendlyError } from "api/UserFriendlyError";
import config from "@/config";
import { adminPanelFeatureToggle } from "../../../utils/feature-toggle-utils";

const MAX_FB_PAGE_ITERATION = 15;
const FACEBOOK_SDK_SCRIPT_ID = "facebook-jssdk";
const FACEBOOK_SDK_SRC = "https://connect.facebook.net/en_US/sdk.js";
const FACEBOOK_SDK_ERROR =
  "It looks like the Facebook pixel is not loaded, so we can't connect to Instagram Insights. Please check if you have an ad blocker enabled.";
let facebookSdkReadyPromise: Promise<void> | null = null;
let initializedFacebookAppId: string | null = null;

export interface FBAccount {
  platformUserId: string;
  first_name?: string;
  last_name?: string;
}

export interface FBPage {
  id: string;
  name: string;
  access_token: string;
  instagram_business_account?: { id: string };
}

export interface FBPageExtended extends FBPage {
  name_with_location_descriptor: string;
  category: unknown;
  category_list: unknown;
  tasks: unknown;
}

export interface InstaAccount {
  id: string;
  ig_id: string;
  username: string;
  name: string;
  followers_count: number;
  profile_picture_url: string;
  website?: string;
}

export interface FBPageWithInsta {
  page: FBPage;
  ig?: InstaAccount;
}

export interface FBGroup {
  id: string;
  name: string;
}

export interface FacebookAuthResponse {
  accessToken: string;
  [key: string]: unknown;
}

interface FacebookLoginOptions {
  scope: string;
  return_scopes: boolean;
  auth_type: string;
  config_id?: string;
  response_type?: string;
  override_default_response_type?: boolean;
}

interface FbLoginOptions {
  useBusinessLogin?: boolean;
}

function isFBSdkOk(): boolean {
  return (window as any).FB && (window as any).FB.login;
}

function createFacebookSdkError() {
  return new UserFriendlyError(FACEBOOK_SDK_ERROR);
}

function getFacebookAppId(useBusinessLogin = true) {
  return getFacebookAuthType(useBusinessLogin) === "business_login"
    ? config.fbBusinessLoginAppId
    : config.fbAppId;
}

function initFacebookSdk(appId: string) {
  window.FB.init({
    appId,
    autoLogAppEvents: true,
    xfbml: true,
    version: config.fbGraphApiVersion,
  });
  initializedFacebookAppId = appId;
}

async function ensureFacebookSdkReady(
  appId = initializedFacebookAppId || config.fbAppId
): Promise<void> {
  if (isFBSdkOk()) {
    if (initializedFacebookAppId !== appId) {
      initFacebookSdk(appId);
    }
    return;
  }

  if (!appId) {
    throw createFacebookSdkError();
  }

  if (!facebookSdkReadyPromise) {
    facebookSdkReadyPromise = new Promise<void>((resolve, reject) => {
      const previousFbAsyncInit = window.fbAsyncInit;
      const fail = () => reject(createFacebookSdkError());
      const finalize = () => {
        if (isFBSdkOk()) {
          resolve();
        } else {
          fail();
        }
      };

      window.fbAsyncInit = () => {
        try {
          previousFbAsyncInit?.();

          if (!window.FB?.init) {
            fail();
            return;
          }

          initFacebookSdk(appId);
          finalize();
        } catch {
          fail();
        }
      };

      const existingScript = document.getElementById(FACEBOOK_SDK_SCRIPT_ID);

      if (!existingScript) {
        const script = document.createElement("script");
        script.id = FACEBOOK_SDK_SCRIPT_ID;
        script.async = true;
        script.src = FACEBOOK_SDK_SRC;
        script.onerror = fail;
        document.head.append(script);
      }

      window.setTimeout(() => {
        if (!isFBSdkOk()) {
          fail();
        }
      }, 10_000);
    }).finally(() => {
      if (!isFBSdkOk()) {
        facebookSdkReadyPromise = null;
      }
    });
  }

  await facebookSdkReadyPromise;
}

export function getFacebookAuthType(useBusinessLogin = true) {
  return useBusinessLogin &&
    config.fbBusinessLoginAppId &&
    config.fbBusinessLoginConfigId &&
    adminPanelFeatureToggle.syncGet("enableFacebookLoginForBusiness")
    ? "business_login"
    : "classic";
}

export async function fbLogin(
  scopes: string[],
  options: FbLoginOptions = {}
): Promise<FacebookAuthResponse> {
  console.log("Requesting scopes: " + scopes);
  const useBusinessLogin = options.useBusinessLogin ?? true;
  const appId = getFacebookAppId(useBusinessLogin);

  if (!appId) {
    throw createFacebookSdkError();
  }

  await ensureFacebookSdkReady(appId);
  return new Promise((resolve, reject) => {
    if (!isFBSdkOk()) {
      return reject(createFacebookSdkError());
    }
    const loginOptions: FacebookLoginOptions = {
      scope: scopes.join(", "),
      return_scopes: true,
      auth_type: "rerequest",
    };
    if (getFacebookAuthType(useBusinessLogin) === "business_login") {
      loginOptions.config_id = config.fbBusinessLoginConfigId;
      loginOptions.response_type = "token";
      loginOptions.override_default_response_type = true;
    }
    window.FB.login(
      (response: { authResponse?: FacebookAuthResponse | null }) => {
        if (!response.authResponse) {
          console.log("User cancelled login or did not fully authorize.");
          reject(
            new UserFriendlyError(
              "No Instagram or Facebook account added: you cancelled the login or did not fully authorise our access."
            )
          );
        } else {
          resolve(response.authResponse);
        }
      },
      loginOptions
    );
  });
}

export async function fbReadMe(auth: FacebookAuthResponse) {
  await ensureFacebookSdkReady();
  return new Promise<FBAccount>((resolve, reject) => {
    if (!isFBSdkOk()) {
      return reject(createFacebookSdkError());
    }
    window.FB.api("/me", { accessToken: auth.accessToken }, (response: any) => {
      if (!response || response.error) {
        return reject(response?.error);
      }
      const account: FBAccount = {
        platformUserId: response.id,
      };
      if (response.name) {
        const [first_name, last_name] = response.name.split(" ");
        account.first_name = first_name;
        account.last_name = last_name;
      }
      resolve(account);
    });
  });
}

const readFbPagePromise = (
  accessToken: string,
  pageUrl: string,
  useShortPages: boolean
) =>
  new Promise((resolve) => {
    window.FB.api(
      pageUrl,
      { accessToken, limit: useShortPages ? 15 : 100 },
      (response: any) => {
        console.log(`${pageUrl} response`, response);
        return resolve(response);
      }
    );
  });

const fbReadPagesCore = async (
  auth: FacebookAuthResponse,
  pageUrl: string,
  useShortPages?: boolean
): Promise<any[]> => {
  await ensureFacebookSdkReady();
  if (!isFBSdkOk()) {
    throw createFacebookSdkError();
  }

  let nextPageUrl = pageUrl;
  let result: Array<any> = [];

  for (
    let i = 0;
    i < (useShortPages ? MAX_FB_PAGE_ITERATION * 2 : MAX_FB_PAGE_ITERATION);
    i++
  ) {
    const response: any = await readFbPagePromise(
      auth.accessToken,
      nextPageUrl,
      useShortPages || false
    );

    if (!response || response.error) {
      throw new Error(response?.error);
    }

    result = [...result, ...response.data];
    if (response.paging?.next) {
      nextPageUrl = response.paging.next;
    } else {
      break;
    }
  }
  return result;
};

export async function fbReadPages(
  auth: FacebookAuthResponse,
  useShortPages: boolean
): Promise<FBPage[]> {
  return await fbReadPagesCore(
    auth,
    "/me/accounts?fields=id,name,access_token,instagram_business_account",
    useShortPages
  );
}

export async function fbReadPagesExtended(
  auth: FacebookAuthResponse,
  useShortPages: boolean
): Promise<FBPageExtended[]> {
  return await fbReadPagesCore(
    auth,
    "/me/accounts?fields=id,name,name_with_location_descriptor,access_token,category,category_list,tasks",
    useShortPages
  );
}

export async function fbReadInstaBizAccount(
  page: FBPage,
  auth: FacebookAuthResponse
): Promise<FBPageWithInsta> {
  await ensureFacebookSdkReady();
  return new Promise((resolve, reject) => {
    if (!isFBSdkOk()) {
      return reject(createFacebookSdkError());
    }
    if (!page.instagram_business_account) {
      return resolve({ page });
    }
    window.FB.api(
      `/${
        page.instagram_business_account!.id
      }?fields=id,ig_id,followers_count,profile_picture_url,name,username,website`,
      { accessToken: auth.accessToken },
      (response: any) => {
        if (!response || response.error) {
          return reject(response?.error);
        }
        console.log("fbData Becomes igAccountData", response);
        // NOTE: this is the data we collect
        resolve({ page, ig: response });
      }
    );
  });
}

export async function fbReadGroups(
  auth: FacebookAuthResponse
): Promise<FBGroup[]> {
  await ensureFacebookSdkReady();
  return new Promise((resolve, reject) => {
    if (!isFBSdkOk()) {
      return reject(createFacebookSdkError());
    }
    window.FB.api(
      "/me/groups?admin_only=true",
      { accessToken: auth.accessToken, limit: 100 },
      (response: any) => {
        if (!response || response.error) {
          return reject(response?.error);
        }
        resolve(response.data);
      }
    );
  });
}
